import {
  Button,
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  FormControlLabel,
  Checkbox,
  Stack,
} from '@mui/material';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRecoilValue, useResetRecoilState } from 'recoil';

import { getParentOrganizations } from '@app/adapter/organization-service';
import { FadeInUpItem } from '@app/components/Company/FadeInUpItem';
import { LetterFadeIn } from '@app/components/Shared/LetterFadeIn';
import {
  attributesBySmaverseSelector,
  locationsTreeSelector,
  searchConditionState,
} from '@app/domain/catalog';
import { useDeviceType } from '@app/hooks/useBrowserHooks';
import { useGADataLayer } from '@app/hooks/useGADataLayer';
import {
  GA_CUSTOM_EVENT,
  ORGANIZATION_STRUCTURE_TYPE,
  SEARCH_OPTIONS,
} from '@app/static/constants';
import { imageAssets } from '@app/theme';
import { CategoryType, CategoryTypeKey } from '@app/types/catalog';
import { Organization } from '@app/types/organization';
import { getChildOrgIds } from '@app/utils/catalog';

interface HomeCarouselProps {
  isLoading?: boolean;
  setSearchType: (type: CategoryTypeKey) => void;
}

export function HomeCarousel({
  setSearchType,
  isLoading,
}: HomeCarouselProps): ReactElement {
  const navigate = useNavigate();
  const currentLocation = useLocation();
  const { isMobile } = useDeviceType();
  const smaverseAttributes = useRecoilValue(attributesBySmaverseSelector);
  const locations = useRecoilValue(locationsTreeSelector);
  const resetSearchConditionState = useResetRecoilState(searchConditionState);
  const [searchType, setSearchTypeLocal] = useState<CategoryTypeKey>(
    CategoryType.EXHIBITION
  );
  const [location, setLocation] = useState<string>('全て');
  const [isSmaverse, setIsSmaverse] = useState<boolean>(false);
  const [organization, setOrganization] = useState('全て');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const { sendGAEvent } = useGADataLayer();
  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await getParentOrganizations({
        expand: ORGANIZATION_STRUCTURE_TYPE.CHILDREN,
      });
      setOrganizations(response.data.value);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  }, []);

  useEffect(() => {
    void fetchOrganizations();
  }, [fetchOrganizations]);

  // NOTE: 下のnavigateを実行している副作用が強制的なルーティングをするためlocation.stateで制御している。
  // 現行仕様は分譲トップへ直接アクセスしても展示場トップへアクセスされるなど、Stateとルーティングの関係がうまく制御できていないよう。
  useEffect(() => {
    if (currentLocation.state?.searchType) {
      setSearchType(currentLocation.state?.searchType);
      setSearchTypeLocal(currentLocation.state?.searchType);
    }
  }, [currentLocation.state, setSearchType]);

  useEffect(() => {
    const params = new URLSearchParams(currentLocation.search);
    if (location && location !== '全て') {
      params.set('locations', location);
    } else {
      params.delete('locations');
    }

    const selectedOrganization = organizations.find(
      (org) => org.id === organization
    );
    if (selectedOrganization) {
      if (searchType === CategoryType.PROPERTY) {
        params.set('org', getChildOrgIds(selectedOrganization));
      } else {
        params.set('org', selectedOrganization.id);
      }
    } else {
      params.delete('org');
    }

    if (searchType === CategoryType.PROPERTY) {
      params.delete('attributes');
      params.set('category', CategoryType.PROPERTY);
    } else {
      params.set('category', CategoryType.EXHIBITION);
      if (isSmaverse && smaverseAttributes.length) {
        const smaverseIds = smaverseAttributes.map((attr) => attr.id);
        params.set('attributes', smaverseIds.join(','));
      } else {
        params.delete('attributes');
      }
    }

    navigate({ search: params.toString() }, { replace: true });
  }, [
    location,
    organization,
    searchType,
    organizations,
    navigate,
    currentLocation.search,
    isSmaverse,
    smaverseAttributes,
  ]);

  const handleSearchTypeChange = (event: SelectChangeEvent<string>) => {
    const newSearchType = event.target.value as CategoryTypeKey;
    setIsSmaverse(false);
    setSearchTypeLocal(newSearchType);
    setSearchType(newSearchType);
  };

  const handleLocationChange = (event: SelectChangeEvent<string>) => {
    setLocation(event.target.value);
  };

  const handleOrganizationChange = (event: SelectChangeEvent<string>) => {
    setOrganization(event.target.value as string);
  };

  const handleClickSearch = () => {
    const params = new URLSearchParams(currentLocation.search);
    const url =
      searchType === CategoryType.EXHIBITION
        ? `/exhibitions?${params.toString()}`
        : `/properties?${params.toString()}`;
    sendGAEvent(GA_CUSTOM_EVENT.TOP_SEARCH, isMobile);

    resetSearchConditionState();
    navigate(url);
  };

  const CustomSelectArrowIcon = () => (
    <div
      style={{
        backgroundColor: '#3287e1',
        clipPath: 'polygon(50% 100%, 0 0, 100% 0)',
        flexShrink: 0,
        height: '10px',
        width: '15px',
      }}
    />
  );

  const ResponsiveFormControl = ({
    children,
  }: {
    children: React.ReactNode;
  }) => (
    <FormControl
      variant={isMobile ? 'standard' : 'outlined'}
      sx={{
        '& .MuiInput-underline:before': {
          borderBottomWidth: 0,
        },
        borderBottom: { sm: 0, xs: '1px solid #3287E1' },
        borderRight: {
          md: '1.5px solid #3287E1',
          sm: '0.5px solid #E0E0E0',
          xs: 'unset',
        },
        mb: { md: 0, sm: 1, xs: 0 },
        minWidth: 150,
        mx: 1,
        pr: 2,
        py: 0.4,
        width: isMobile ? '100%' : 'auto',
      }}
    >
      {children}
    </FormControl>
  );

  return (
    <Stack
      alignItems="center"
      bgcolor="#E5F2F8"
      sx={{
        overflow: 'hidden',
        pb: { md: 0, xs: 6 },
        position: 'relative',
        pt: { md: '3.5rem', xs: '2rem' },
      }}
    >
      <Stack
        maxWidth={1100}
        alignItems="center"
        width={'100%'}
        gap={7}
        sx={{
          alignItems: {
            md: 'start',
            xs: 'center',
          },
          px: { md: 5, xs: 2.5 },
        }}
      >
        <Stack position="relative" width="100%" alignItems="start" gap={3}>
          <FadeInUpItem>
            <Stack
              direction="row"
              alignItems="center"
              bgcolor="#3287e1"
              gap={2}
              borderRadius={20}
              px={1.5}
              py={0.5}
              zIndex={2}
            >
              <Box
                sx={{ height: { md: 16, xs: 10 }, width: { md: 16, xs: 10 } }}
                borderRadius="100%"
                bgcolor="#55bffe"
              />
              <Typography
                component="div"
                sx={{
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: 'clamp(1.125rem, 0.7837rem + 1.4vw, 1.625rem)',
                  fontWeight: 'bold',
                }}
              >
                {searchType === CategoryType.PROPERTY ? '分譲地' : '住宅展示場'}
                見学予約サービス
              </Typography>
            </Stack>
          </FadeInUpItem>

          <LetterFadeIn
            style={{
              fontSize: 'clamp(2.75rem, 1.8944rem + 3.51vw, 4rem)',
              fontWeight: 700,
              lineHeight: 1.5,
              zIndex: 2,
            }}
            text="理想の住まいを<br/>見つける"
          />

          <LetterFadeIn
            style={{
              color: '#3287e1',
              fontSize: 'clamp(1.125rem, 0.6107rem + 2.11vw, 1.875rem)',
              fontWeight: 700,
              lineHeight: 1.5,
              zIndex: 2,
            }}
            text="Find your ideal home"
          />

          <Box
            component="img"
            src={imageAssets.imageTopAsset}
            sx={{
              maxWidth: 540,
              objectFit: 'contain',
              position: 'absolute',
              right: {
                md: -20,
                xs: -90,
              },
              top: {
                md: -40,
                sm: '20%',
                xs: '95%',
              },
            }}
          />
        </Stack>
        <FadeInUpItem>
          <Box
            position="relative"
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems="center"
            justifyContent="center"
            p={1.2}
            width="100%"
            sx={{
              bgcolor: {
                md: 'unset',
                xs: 'rgba(255, 255, 255, 1)',
              },
              borderRadius: {
                lg: '80px',
                sm: 5,
                xs: 5,
              },
              boxShadow: {
                md: 'unset !important',
                xs: '9px 8px 10px rgba(42, 69, 73, 0.3) !important',
              },
              flexShrink: 0,
              minWidth: { md: 850, sm: 'unset' },
              width: { sm: '100%', xs: 300 },
              zIndex: 3,
            }}
          >
            <Box
              component="img"
              src={imageAssets.searchTopBackground}
              sx={{
                bottom: 3,
                display: { md: 'block', xs: 'none' },
                filter: 'drop-shadow(10px 10px 10px rgba(42, 69, 73, 0.4))',
                height: 'auto',
                left: -10,
                mt: 6,
                objectFit: 'contain',
                position: 'absolute',
                width: '105%',
              }}
            />
            <Stack
              sx={{
                alignItems: { md: 'center', sm: 'end' },
                borderBottom: { md: 0, sm: 1 },
                borderColor: { sm: '#3287E1' },
                flexDirection: { md: 'auto', sm: 'row', xs: 'column' },
                width: { md: 'auto', xs: '100%' },
              }}
            >
              <ResponsiveFormControl>
                <Select
                  variant="standard"
                  value={searchType}
                  onChange={handleSearchTypeChange}
                  displayEmpty
                  disableUnderline
                  IconComponent={CustomSelectArrowIcon}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <b>住宅展示場を探す</b>;
                    }
                    return (
                      <b>
                        {SEARCH_OPTIONS.find(
                          (option) => option.value === selected
                        )?.label || ''}
                      </b>
                    );
                  }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&:hover': {
                      color: '#3287e1',
                    },
                    fontSize: 14,
                    ml: -1.5,
                    textAlign: 'left',
                    transition: 'color 0.3s ease',
                  }}
                >
                  {SEARCH_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </ResponsiveFormControl>

              <ResponsiveFormControl>
                <Stack
                  sx={{
                    alignItems: { md: 'flex-start', xs: 'center' },
                    flexDirection: { md: 'column', xs: 'row' },
                    gap: { md: 0, xs: 1 },
                    width: { sm: '100%', xs: 'auto' },
                  }}
                >
                  <Typography
                    fontSize={10}
                    sx={{
                      display: { sm: 'none', xs: 'block' },
                      flexShrink: 0,
                      minWidth: '30px',
                    }}
                  >
                    エリア
                  </Typography>

                  <InputLabel
                    shrink={!!location}
                    sx={{
                      display: { sm: 'block', xs: 'none' },
                      fontSize: '0.8rem',
                      left: { md: 0, sm: 10 },
                      top: { md: 6, sm: 0 },
                    }}
                  >
                    エリア
                  </InputLabel>

                  <Select
                    variant="standard"
                    value={location}
                    onChange={handleLocationChange}
                    displayEmpty
                    disableUnderline
                    IconComponent={CustomSelectArrowIcon}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:hover': {
                        color: '#3287e1',
                      },
                      mt: 'unset !important',
                      textAlign: 'left',
                      transition: 'color 0.3s ease',
                      width: { md: 150, xs: '100%' },
                    }}
                  >
                    <MenuItem value="全て">全て</MenuItem>
                    {locations.flatMap(
                      (region) =>
                        region.children?.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.name}
                          </MenuItem>
                        )) || []
                    )}
                  </Select>
                </Stack>
              </ResponsiveFormControl>

              <ResponsiveFormControl>
                <Stack
                  sx={{
                    alignItems: { md: 'flex-start', xs: 'center' },
                    flexDirection: { md: 'column', xs: 'row' },
                    gap: { md: 0, xs: 1 },
                    width: { sm: '100%', xs: 'auto' },
                  }}
                >
                  <Typography
                    fontSize={10}
                    sx={{
                      display: { sm: 'none', xs: 'block' },
                      flexShrink: 0,
                      minWidth: '30px',
                    }}
                  >
                    企業
                  </Typography>

                  <InputLabel
                    shrink={!!organization}
                    sx={{
                      display: { sm: 'block', xs: 'none' },
                      fontSize: '0.8rem',
                      left: { md: 0, sm: 10 },
                      top: { md: 6, sm: 0 },
                    }}
                  >
                    企業
                  </InputLabel>

                  <Select
                    variant="standard"
                    value={organization}
                    onChange={handleOrganizationChange}
                    displayEmpty
                    disableUnderline
                    IconComponent={CustomSelectArrowIcon}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:hover': {
                        color: '#3287e1',
                      },
                      mt: 'unset !important',
                      textAlign: 'left',
                      transition: 'color 0.3s ease',
                      width: { md: 150, xs: '100%' },
                    }}
                  >
                    <MenuItem value="全て">全て</MenuItem>
                    {organizations.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
              </ResponsiveFormControl>
            </Stack>

            <Stack
              sx={{
                alignItems: { md: 'center', sm: 'end' },
                flexDirection: { md: 'row', sm: 'row', xs: 'column' },
                mt: { md: 0, sm: 1.5 },
              }}
            >
              {searchType === CategoryType.EXHIBITION && (
                <FormControlLabel
                  disabled={!smaverseAttributes.length || isLoading}
                  control={
                    <Checkbox
                      size="small"
                      checked={isSmaverse}
                      onChange={() => setIsSmaverse(!isSmaverse)}
                      sx={{
                        '&.Mui-checked': {
                          color: '#3287e1',
                        },
                        '.MuiFormControlLabel-root:hover &': {
                          color: '#3287e1',
                        },
                        color: '#999',
                        transition: 'color 0.3s ease',
                      }}
                    />
                  }
                  label={
                    <Typography fontSize={14}>スマバース体験のみ</Typography>
                  }
                  sx={{
                    '&:hover': {
                      color: '#3287e1',
                    },
                    flexShrink: 0,
                    transition: 'color 0.3s ease',
                    userSelect: 'none',
                    zIndex: 2,
                  }}
                />
              )}
              <Button
                color="primary"
                onClick={handleClickSearch}
                sx={{
                  '&:hover': {
                    bgcolor: 'white',
                    border: '1px solid #3287e1',
                    color: '#3287e1',
                  },
                  bgcolor: '#3287e1',
                  border: '1px solid transparent',
                  borderRadius: 3,
                  color: 'white',
                  flexShrink: 0,
                  mt: { md: 0, xs: 2 },
                  mx: 2,
                  transition: 'all 0.3s ease',
                  width: '114px',
                }}
              >
                検索する
              </Button>
            </Stack>
          </Box>
        </FadeInUpItem>
      </Stack>

      <Box
        component="img"
        src={imageAssets.arrivalBackground}
        sx={{
          display: { md: 'block', xs: 'none' },
          height: 'auto',
          mt: 6,
          objectFit: 'contain',
          width: '100%',
        }}
      />
    </Stack>
  );
}
